import React from 'react';
import styled from 'styled-components';
import { Box, ProgressBar } from '@99designs/emu';
import { boxShadows } from '@99designs/emu/settings';
import { Icon } from '@99designs/frontend-components/components/Icon';
import { getComponentClasses } from '@99designs/frontend-components/utils/app';
import { IconPlus2, IconTrash2 } from '@99designs/icons';
import { OnWhiteIconButton } from '@99designs/spa-common';

export interface Props {
    name: string;
    onDelete: () => void;
    children: React.ReactNode;
}

export const AttachmentFrame = styled.div`
    position: relative;
    background: white;
    box-shadow: ${boxShadows.low};
`;

export const Attachment = (props: Props) => (
    <Box>
        <AttachmentFrame>{props.children}</AttachmentFrame>
        <Box paddingX="0.75bu" paddingY="1bu">
            <Meta {...props} />
        </Box>
    </Box>
);

export const AttachmentPreview = (props: { previewable?: boolean; url?: string }) => (
    <UploaderAttachmentOverlay>
        {props.previewable ? (
            /* eslint-disable-next-line jsx-a11y/alt-text */
            <img src={props.url} className="uploader-attachment-thumbnail" />
        ) : (
            <Icon type={'document-1'} variations={['2x']} />
        )}
    </UploaderAttachmentOverlay>
);

export const UploaderAttachmentFileInput = ({
    onClick,
    children,
}: {
    onClick: (target: HTMLInputElement) => void;
    children: React.ReactNode;
}) => (
    <div className="uploader-attachment-file-input">
        <UploaderAttachmentOverlay variations={['outline']}>
            <div className="uploader-attachment-overlay__content__icon">
                <IconPlus2 />
            </div>
            <div className="uploader-attachment-overlay__content__label">{children}</div>
        </UploaderAttachmentOverlay>
        <input
            key={Date.now()}
            type="file"
            aria-label={__('Upload files')}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => onClick(e.target)}
            className="uploader-attachment-file-input__target"
            multiple
        />
    </div>
);

export const UploaderAttachmentProgress = ({
    completed,
    total,
}: {
    completed: number;
    total: number;
}) => {
    const percentage = Math.min(Math.round((completed / total) * 100), 100);
    return (
        <UploaderAttachmentOverlay variations={['shaded']}>
            <div className="heading heading--h5">{percentage + '%'}</div>
            <div className="uploader-attachment-overlay__progress-bar">
                <ProgressBar small progress={completed} aria-label="Upload progress" />
            </div>
        </UploaderAttachmentOverlay>
    );
};

const Meta = (props: { name: string; onDelete: () => void }) => (
    <div className="uploader-attachment-file-meta">
        <div className="uploader-attachment-file-meta__action">
            <OnWhiteIconButton onClick={props.onDelete}>
                <IconTrash2 fontSize={14} opacity={0.75} />
            </OnWhiteIconButton>
        </div>
        <div className="uploader-attachment-file-meta__label">
            <span>{props.name}</span>
        </div>
    </div>
);

const UploaderAttachmentOverlay = ({
    children,
    variations,
}: {
    variations?: string[];
    children: React.ReactNode;
}) => {
    const classes = getComponentClasses('uploader-attachment-overlay', variations);
    const innerClasses = getComponentClasses('uploader-attachment-overlay__inner', variations);

    return (
        <div className={classes}>
            <div className={innerClasses}>
                <div className="uploader-attachment-overlay__content">{children}</div>
            </div>
        </div>
    );
};
